'use client';

import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { faSliders } from '@fortawesome/pro-regular-svg-icons';
import { useLayoutControl } from 'src/context/layout';
import { useAuth } from 'src/context/auth';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { PROFILE_CUSTOMIZATION_READ } from 'src/components/PermissionGuard/checks';
import { Typography, ProfileSelectorDropdown } from 'src/components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { EnrichingCodesInfo } from 'src/components/PulseUI';
import useProfileSelections from 'src/hooks/profiles/useProfileSelections';
import { exists } from 'src/utils/exists/exists';

const ProfileFilterBar = ({
  location,
  disableSticky = false,
  inDrawer = false,
}: {
  location?: 'hcp' | 'center' | 'clinic' | 'system' | 'site_of_care';
  disableSticky?: boolean;
  inDrawer?: boolean;
}) => {
  const { dataCoverage: dataCoverageFlag } = useFlags();
  const { user } = useAuth();
  const { hasPermission } = useAuthorization();
  const shouldDisableSticky =
    disableSticky ||
    (!!user?.plays_enabled && hasPermission(PROFILE_CUSTOMIZATION_READ));
  const { scrollValue } = useLayoutControl();
  const [isSticky, setIsSticky] = useState(false);
  const { selectedFilterSet, selectedCohort } = useProfileSelections(
    location ?? 'hcp'
  );
  const dataCoverage = selectedFilterSet?.detailed_filters?.flatMap(
    (filter) =>
      filter.detailed_values?.map((value: any) => value.vrdc_enriched) ?? []
  );

  useEffect(() => {
    if (shouldDisableSticky) {
      setIsSticky(false);
      return;
    }
    if (scrollValue >= 100) {
      setIsSticky(true);
    } else {
      setIsSticky(false);
    }
  }, [scrollValue, shouldDisableSticky]);

  // Content of the filter bar that's consistent in both states
  const filterContent = (
    <>
      <Icon icon={faSliders} color="#171A1C" aria-hidden="true" />
      <Typography variant="body1" bold color="primary">
        Filter
      </Typography>
      <ProfileSelectorDropdown location={location} />
    </>
  );

  const enrichingCodesContent = (selectedFilterSet || selectedCohort) &&
    dataCoverageFlag && (
      <EnrichingCodesInfo
        dataCoverage={dataCoverage}
        isEncounter={exists(selectedCohort)}
      />
    );

  return (
    <>
      {/* Spacer when sticky */}
      {isSticky && (
        <div className="h-16" data-testid="profile-filter-bar-spacer" />
      )}

      {isSticky ? (
        // Sticky state - positioned below sticky ProfileHeader
        <div
          role="region"
          aria-label="Profile Filter"
          data-testid="profile-filter-bar"
          className="sticky top-0 h-16 z-[5] bg-white border-b border-neutral-200"
        >
          <div className="px-4 w-full max-w-7xl mx-auto h-full">
            <div className="flex flex-row items-center gap-2 h-full">
              <div className="flex flex-row items-center gap-2 flex-1 min-w-0">
                {filterContent}
              </div>
              {enrichingCodesContent}
            </div>
          </div>
        </div>
      ) : (
        // Non-sticky state
        <div
          role="region"
          aria-label="Profile Filter"
          data-testid="profile-filter-bar"
          className="w-full bg-white py-2 rounded-lg border border-neutral-200 shadow-sm mt-4 mb-4"
        >
          <div className="px-4 w-full max-w-7xl mx-auto">
            <div
              className={`flex gap-2 py-2 ${
                inDrawer ? 'flex-col items-start' : 'flex-row items-center'
              }`}
            >
              <div
                className={`flex flex-row items-center gap-2 min-w-0 ${
                  inDrawer ? 'w-full' : 'flex-1'
                }`}
              >
                {filterContent}
              </div>
              {enrichingCodesContent}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileFilterBar;
